import React from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../components/common/Button';
import ConfirmationModal from '../components/ConfirmationModal';
import Toast from '../components/Toast';
import { useAdminAgencies } from '../hooks/useAdminAgencies';
import AdminAgencyForm from '../components/admin/AdminAgencyForm';
import AdminAgencyList from '../components/admin/AdminAgencyList';

export default function AdminAgenciesPage() {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'ar';

    const {
        cities,
        newAgency,
        setNewAgency,
        editingAgency,
        setEditingAgency,
        cityFilter,
        setCityFilter,
        nameSearch,
        setNameSearch,
        toast,
        setToast,
        isEditAgencyModalOpen,
        setIsEditAgencyModalOpen,
        isDeleteAgencyModalOpen,
        setIsDeleteAgencyModalOpen,
        isResetPasswordModalOpen,
        setIsResetPasswordModalOpen,
        agencyToDelete,
        setAgencyToDelete,
        agencyToReset,
        setAgencyToReset,
        handleCreateAgency,
        handleDeleteAgency,
        confirmDeleteAgency,
        handleResetPassword,
        confirmResetPassword,
        handleEditAgencyClick,
        handleEditAgencySubmit,
        filteredAgencies
    } = useAdminAgencies(t, i18n);

    return (
        <div className="space-y-6">
            {/* Create Agency Form */}
            <AdminAgencyForm
                t={t}
                i18n={i18n}
                newAgency={newAgency}
                setNewAgency={setNewAgency}
                cities={cities}
                handleCreateAgency={handleCreateAgency}
                isRTL={isRTL}
            />

            {/* Agencies List and Filters */}
            <AdminAgencyList
                t={t}
                i18n={i18n}
                filteredAgencies={filteredAgencies}
                cities={cities}
                handleEditAgencyClick={handleEditAgencyClick}
                handleDeleteAgency={handleDeleteAgency}
                handleResetPassword={handleResetPassword}
                cityFilter={cityFilter}
                setCityFilter={setCityFilter}
                nameSearch={nameSearch}
                setNameSearch={setNameSearch}
            />

            {/* Edit Agency Modal */}
            {isEditAgencyModalOpen && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
                    <div className="relative bg-bg-card border border-gold/20 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
                        <div className="flex justify-between items-center p-6 pb-4 border-b border-gold/10">
                            <h3 className="text-xl font-bold text-text">{t('editAgency')}</h3>
                            <button onClick={() => setIsEditAgencyModalOpen(false)} className="text-textMuted hover:text-white">
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <form onSubmit={handleEditAgencySubmit} className="flex flex-col flex-1 overflow-hidden">
                            <div className="overflow-y-auto px-6 py-4 space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-text-muted mb-1">{t('agencyNameEnglish')} <span className="text-danger-500">*</span></label>
                                        <input type="text" className="bg-bg border border-gold/20 text-text rounded px-3 py-2 w-full mb-2 focus:ring-gold focus:border-gold" value={editingAgency.name_en || ''} onChange={e => setEditingAgency({ ...editingAgency, name_en: e.target.value })} required />
                                        <label className="block text-sm font-medium text-text-muted mb-1">{t('agencyNameArabic')}</label>
                                        <input type="text" className="bg-bg border border-gold/20 text-text rounded px-3 py-2 w-full text-right focus:ring-gold focus:border-gold" dir="rtl" value={editingAgency.name_ar || ''} onChange={e => setEditingAgency({ ...editingAgency, name_ar: e.target.value })} />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-textMuted mb-1">{t('clientEmail')}</label>
                                        <input type="email" className="border border-gold/10 rounded px-3 py-2 w-full bg-neutralDark/30 text-textMuted cursor-not-allowed" value={editingAgency.client_email} disabled />
                                        <p className="text-xs text-textMuted/70 mt-1">{t('emailReadOnly')}</p>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-textMuted mb-1">{t('managerName')}</label>
                                        <input type="text" className="bg-neutralDark/50 border border-gold/20 text-textPrimary rounded px-3 py-2 w-full focus:ring-gold focus:border-gold" value={editingAgency.manager_name || ''} onChange={e => setEditingAgency({ ...editingAgency, manager_name: e.target.value })} />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-textMuted mb-1">{t('phone')}</label>
                                        <input type="text" className="bg-neutralDark/50 border border-gold/20 text-textPrimary rounded px-3 py-2 w-full focus:ring-gold focus:border-gold" value={editingAgency.phone || ''} onChange={e => setEditingAgency({ ...editingAgency, phone: e.target.value })} />
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-textMuted mb-1">{t('city')}</label>
                                        <select className="bg-neutralDark/50 border border-gold/20 text-textPrimary rounded px-3 py-2 w-full focus:ring-gold focus:border-gold" value={editingAgency.city || ''} onChange={e => setEditingAgency({ ...editingAgency, city: e.target.value })}>
                                            <option value="">{t('selectCity')}</option>
                                            {(cities || []).map(city => <option key={city.en} value={city.en}>{i18n.language === 'ar' ? city.ar : city.en}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-textMuted mb-1">{t('address')}</label>
                                        <input type="text" className="bg-neutralDark/50 border border-gold/20 text-textPrimary rounded px-3 py-2 w-full focus:ring-gold focus:border-gold" value={editingAgency.address || ''} onChange={e => setEditingAgency({ ...editingAgency, address: e.target.value })} />
                                    </div>
                                </div>

                                {/* New Fields: Website & Facebook */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-textMuted mb-1">{t('website')}</label>
                                        <input type="url" className="bg-neutralDark/50 border border-gold/20 text-textPrimary rounded px-3 py-2 w-full focus:ring-gold focus:border-gold" value={editingAgency.website || ''} onChange={e => setEditingAgency({ ...editingAgency, website: e.target.value })} placeholder={t('websitePlaceholder')} />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-textMuted mb-1">{t('facebook')}</label>
                                        <input type="url" className="bg-neutralDark/50 border border-gold/20 text-textPrimary rounded px-3 py-2 w-full focus:ring-gold focus:border-gold" value={editingAgency.facebook || ''} onChange={e => setEditingAgency({ ...editingAgency, facebook: e.target.value })} placeholder={t('facebookPlaceholder')} />
                                    </div>
                                </div>

                                {/* Description Field */}
                                <div>
                                    <label className="block text-sm font-medium text-textMuted mb-1">{t('description')}</label>
                                    <textarea
                                        className="bg-neutralDark/50 border border-gold/20 text-textPrimary rounded px-3 py-2 w-full h-24 focus:ring-gold focus:border-gold"
                                        value={editingAgency.description || ''}
                                        onChange={e => setEditingAgency({ ...editingAgency, description: e.target.value })}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-textMuted mb-1">{t('agencyLogo')}</label>
                                    <input
                                        type="file" accept="image/*"
                                        className="bg-neutralDark/50 border border-gold/20 text-textMuted rounded px-3 py-2 w-full file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-gold file:text-primary hover:file:bg-gold-light"
                                        onChange={e => setEditingAgency({ ...editingAgency, logo: e.target.files[0] })}
                                    />
                                </div>
                            </div>
                            <div className="flex justify-end gap-3 p-6 border-t border-gold/10 bg-gray-50 rounded-b-lg">
                                <button type="button" onClick={() => setIsEditAgencyModalOpen(false)} className="bg-transparent py-2 px-4 border border-gold/20 rounded-md shadow-sm text-sm font-medium text-text-muted hover:bg-gold/10 hover:text-gold transition-colors">{t('cancel')}</button>
                                <Button type="submit" variant="primary">
                                    {t('save')}
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Confirmation Modals */}
            <ConfirmationModal
                isOpen={isResetPasswordModalOpen}
                onClose={() => {
                    setIsResetPasswordModalOpen(false);
                    setAgencyToReset(null);
                }}
                onConfirm={confirmResetPassword}
                title={t('resetPassword')}
                message={t('confirmResetPassword')}
                confirmText={t('yes')}
                cancelText={t('cancel')}
                isDanger={false}
            />

            <ConfirmationModal
                isOpen={isDeleteAgencyModalOpen}
                onClose={() => {
                    setIsDeleteAgencyModalOpen(false);
                    setAgencyToDelete(null);
                }}
                onConfirm={confirmDeleteAgency}
                title={t('deleteAgency')}
                message={t('confirmDeleteAgency')}
                confirmText={t('delete')}
                cancelText={t('cancel')}
                isDanger={true}
            />

            {/* Toast Notification */}
            <Toast
                message={toast.message}
                type={toast.type}
                isVisible={toast.isVisible}
                onClose={() => setToast({ ...toast, isVisible: false })}
                duration={5000}
            />
        </div>
    );
}
